import { Record, List, Map } from 'immutable';
import { getDefaultLocale } from '@/labels/locale';
import type { CompanyPermissions, RoleModificators } from '@/domain/env/schemas';
import type {
  AppShape,
  EnvShape,
  OrgBotShape,
  PayModuleCompanyPermissionShape,
  PermissionsNotesShape,
  PermissionsShape,
  UserFeaturesShape,
} from '@/domain/env/types';

import { DEFAULT_PANEL_WIDTH } from '@/domain/env/constants';

import botPic from '@/domain/env/images/bot.png';

export const PermissionsNotesFactory = Record<PermissionsNotesShape>({
  isCreate: false,
  isUpdate: false,
  isDelete: false,
});

export const PermissionsFactory = Record<PermissionsShape>({
  isDokkaSupport: false,
  superUser: false,
  notes: PermissionsNotesFactory(),
});

export const OrgBotFactory = Record<OrgBotShape>({
  userId: '',
  username: 'BOT',
  picture: botPic,
});

export const UserFeaturesFactory = Record<UserFeaturesShape>({
  modal: true,
  filter_documents_tagged_by_user_only: false,
});

export const roleModificatorsFactory = Record<RoleModificators>({
  limited: false,
  restricted: false,
  readonly: false,
});

// Defaults exist only because a Record needs them: every entry arrives with all
// five fields, validated by CompanyPermissionsSchema.
export const CompanyPermissionsFactory = Record<CompanyPermissions>({
  role: 'user',
  restricted: false,
  limited: false,
  readonly: false,
  displayRole: '',
});

export const appFactory = Record<AppShape>({
  active: false,
  appName: '',
  appUrl: '',
  companiesContext: [],
});

export const PayModuleCompanyPermissionFactory = Record<PayModuleCompanyPermissionShape>({
  accountId: '',
  isApprover: false,
  isController: false,
  isOwner: false,
});

export const EnvFactory = Record<EnvShape>({
  dokkaToken: '',
  role: null,
  build: process.env.REACT_APP_BUILD_VERSION,
  rtl: getDefaultLocale() === 'he',
  locale: getDefaultLocale(),
  loading: 0,
  isSagaDataLoading: false,
  linkedSidebarWidth: DEFAULT_PANEL_WIDTH,
  paymentView: Map(),
  chatToken: '',
  chatUser: '',
  organizationId: '', // empty indicating default value, backend will send real id or null
  organizationName: '',
  firstName: '',
  lastName: '',
  picture: '',
  phone: '',
  permissions: PermissionsFactory(),
  superUser: false,
  isOrgAdmin: false,
  isDokkaSupport: false,
  backSearchUrl: null,
  persistedBackUrl: null,
  userId: null,
  navigation: Map(),
  isExpired: false,
  isPermissionsChanged: false,
  userGUID: null,
  title: '',
  label: null,
  newChatFlow: false,
  googleSearchText: null,
  googlePanelStatus: false,
  appStatuses: List(),
  helpdeskLink: null,
  workSpace: Map(),
  organizationBot: OrgBotFactory(),
  features: UserFeaturesFactory(),
  allowFreshdesk: false,
  error: '',
  supplierAliases: '',
  roleModificators: roleModificatorsFactory(),
  companyPermissions: Map(),
  connectionHealth: true,
  cognitoUsername: null,
  apps: Map(),
  beta: false,
  isRecordingEnabled: false,
  payModuleCompanyPermissions: List(),
  isPayModuleCompanyPermissionsLoaded: false,
});
